import { useRef, useEffect } from "react";

export function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function getAoiOptions(
  dataState,
  aoiCategories,
  selectedAoiCat,
  aoiDescriptionField = null
) {
  const aoiDataId = aoiCategories.find(
    (aoiCat) => aoiCat.id === selectedAoiCat
  ).data;
  if (dataState[aoiDataId].fetched) {
    return dataState[aoiDataId].data.features.map((feature) => {
      return {
        id: feature.id,
        label: aoiDescriptionField
          ? `${feature.properties.name} / ${feature.properties[aoiDescriptionField]}`
          : feature.properties.name,
      };
    });
  }
  return [];
}

// custom MUI data grid filter function for cases where two values are selected for a column by user (e.g. IRB and UCRB)
export const multiSelectFilterFn = (filterItem, column) => {
  if (!filterItem.field || !filterItem.value || !filterItem.operator) {
    return null;
  }
  // Ensure filterItem.value is an array
  const filterValues = Array.isArray(filterItem.value)
    ? filterItem.value
    : [filterItem.value];
  if (filterValues.length === 0) {
    return true;
  }
  return (value, row, column, apiRef) => {
    // Split the cell value into an array of categories
    const rawValue = value.value;
    let cellValues = [];

    if(typeof rawValue === 'string'){
     cellValues = value.value?.split(/[,;]/) || [];
    } // else is object with name property
    else if (rawValue && typeof rawValue === 'object') {
      console.log(rawValue);
      cellValues = value.value.name?.split(/[,;]/) || [];
    }
    // Check if any of the categories in the cell value are included in the filter values
    if (cellValues.length > 0) {
      return cellValues.some((cellValue) =>
        filterValues.includes(cellValue.trim())
      );
    }
    return false;
  };
};

// https://saimirkapaj.com/componentdidupdate-with-react-hooks
// custom hook function similar to useEffect but that doesn't
//  run effects on initial render
export function useDidUpdate(callback, dependencies) {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      callback();
    } else {
      didMount.current = true;
    }
  }, [callback, dependencies]);
}


export function getUrlParams() {
  const paramString = window.location.search;
  if (paramString.includes("=")) {
    let searchParams = new URLSearchParams(paramString);
    // transform key/value pairs to object
    let urlParams = Object.fromEntries(searchParams);
    Object.keys(urlParams).forEach(key => {
      urlParams[key] = JSON.parse(urlParams[key])
    })
    return urlParams
  } else{
    return paramString;
  }
}

export function updateUrl(props) {
  let {
    aoiCategory,
    aoiSelectedIds,
    lat,
    lon,
    zoom,
    layerState,
    uiState,
    startDate,
    endDate,
  } = props.main;

  // any state that is always present can be set by default (e.g. map center & zoom)
  let urlParams = {};
  urlParams.embedded = {}
  urlParams.main = {
    lat: lat,
    lon: lon,
    zoom: zoom,
  };

  // check other props and add to urlParams if not undefined
  if (aoiCategory) {
    urlParams.main.aoiCategory = aoiCategory;
  }
  if (aoiSelectedIds) {
    urlParams.main.aoiSelectedIds = aoiSelectedIds;
  }

  // helper to format date as YYYY-MM-DD
  const yyyymmdd = (date) => {
    const mm = date.getMonth() + 1; // getMonth() is zero-based
    const dd = date.getDate();

    return [date.getFullYear(),
            (mm>9 ? '' : '0') + mm,
            (dd>9 ? '' : '0') + dd
           ].join('-');
  };

  if(startDate && endDate){
    urlParams.main.startDate = yyyymmdd(new Date(startDate));
    urlParams.main.endDate = yyyymmdd(new Date(endDate));
  }

  // map through layers and create array of active layer ids, plot ids
  urlParams.main.activeLayers = [];
  urlParams.main.colorValues = [];
  urlParams.main.activeFilters = [];

  Object.keys(layerState).forEach((id) => {
    if (layerState[id].showLayer) {
      urlParams.main.activeLayers.push(id);
      urlParams.main.colorValues.push(layerState[id].colorValue);
      urlParams.main.activeFilters.push(JSON.stringify(layerState[id].activeFilters));
    }
  });

  if (urlParams.main.activeLayers.length === 0) {
    urlParams.main.activeLayers.push("none");
  }

  // add uiState (active tab in content container)
  if (uiState) {
    if (uiState.selectedTab) {
      urlParams.main.selectedTab = uiState.selectedTab;
    }
  }

  const currentUrlParams = urlParams.main;
  const escapedParams = Object.keys(currentUrlParams)
    .map(
      (k) => `${k}=${encodeURIComponent(JSON.stringify(currentUrlParams[k]))}`
    )
    .join("&");

  const url =
    window.location.origin + window.location.pathname + "?" + escapedParams;

  // add entry to browser's session history
  // TODO: if map center/zoom is stored, don't push state to browser history (use replaceState instead of pushState)
  window.history.pushState({}, "", url);
}

export const randomColor = () => {
  const colorPallete = [
    "#ff7c43",
    "#665191",
    "#a05195",
    "#d45087",
    "#f95d6a"
  ];

  // const uniqueValues = [
  //   ...new Set(
  //     features.map((feat) => {
  //       return feat.properties[colorValue];
  //     })
  //   ),
  // ];

  const randomIndex = [Math.floor(colorPallete.length * Math.random())];
  return colorPallete[randomIndex];
};

// construct url to query CWIS 1.0 api to get current site info
// see https://code.usgs.gov/noms/nwd/-/blob/master/deploy/lib/L-cwis-2021.09.01/L-cwis.js
export const getCwisUrl = (siteId) => {
  const baseUrl =
    "https://dashboard.waterdata.usgs.gov/service/cwis/1.0/odata/CurrentConditions";

  // default site properties to retrieve
  // for list of available see [CWIS_ROOT_URL]/odata/$metadata#CurrentConditions
  const cwisProperties = [
  //   "AgencyCode", //              string, not null
    "SiteNumber", //              string, not null
    "SiteName", //                string, not null
    "SiteTypeCode", //            string, null
  //   "Latitude", //                float,  not null
  //   "Longitude", //               float,  not null
    "CurrentConditionID", //      int,    not null
    "ParameterCode", //           string, not null
    "TimeLocal", //               string, not null
    "TimeZoneCode", //            string, not null
    "Value", //                   float,  null
    "ValueFlagCode", //           string, null
    "RateOfChangeUnitPerHour", // float,  null
    "StatisticStatusCode", //     null, 'NR_NOMEAS', 'NR_0FLOW', 'NR_REVFLOW', 'P0', 'P0_10', 'P10_25', 'P25_75', 'P75_90', 'P90_100', 'P100'
    "FloodStageStatusCode", //     null, 'NA', 'NOMEAS', '>MAJ', '>MOD', '>MNR', '>ACT', 'NOFLOOD'
  ];

  // max number of sites (cap to prevent very large service responses)
  // const nSitesMax = 1000;

  const res = `${baseUrl}/?$top=50&$filter=(SiteNumber in ("${siteId}"))&$select=${cwisProperties.join(',')}&$orderby=TimeLocal%20desc`;
  // console.log(res);
  return res;
};


// returns UTC offset from timezone code
// adapted from https://code.usgs.gov/noms/nwd/-/blob/master/deploy/lib/L-cwis-2021.09.01/L-cwis.js
export const getTimeZoneCodeOffset = (timeZoneCode) => {
  const TimeZoneCodeOffset = {
    ACSST :'+10:30',    ACST :'+09:30',    ADT  :'-03:00',    AESST  :'+11:00',    AEST  :'+10:00',    AFT   :'+04:30',    AKDT:'-08:00',
    AKST  :'-09:00',    AST  :'-04:00',    AWSST:'+09:00',    AWST   :'+08:00',    BST   :'+01:00',    BT    :'+03:00',    CADT:'+10:30',
    CAST  :'+09:30',    CCT  :'+08:00',    CDT  :'-05:00',    CET    :'+01:00',    CETDST:'+02:00',    CST   :'-06:00',    DNT :'+01:00',
    DST   :'+01:00',    EASST:'+11:00',    EAST :'+10:00',    EDT    :'-04:00',    EET   :'+02:00',    EETDST:'+03:00',    EST :'-05:00',
    FST   :'+01:00',    FWT  :'+02:00',    GMT  :'+00:00',    GST    :'+10:00',    HDT   :'-09:00',    HST   :'-10:00',    IDLE:'+12:00',
    IDLW  :'-12:00',    IST  :'+02:00',    IT   :'+03:30',    JST    :'+09:00',    JT    :'+07:30',    KST   :'+09:00',    LIGT:'+10:00',
    MDT   :'-06:00',    MEST :'+02:00',    MET  :'+01:00',    METDST :'+02:00',    MEWT  :'+01:00',    MEZ   :'+01:00',    MST :'-07:00',
    MT    :'+08:30',    NDT  :'-02:30',    NFT  :'-03:30',    NOR    :'+01:00',    NST   :'-03:30',    NZDT  :'+13:00',    NZST:'+12:00',
    NZT   :'+12:00',    PDT  :'-07:00',    PST  :'-08:00',    SADT   :'+10:30',    SAT   :'+09:30',    SET   :'+01:00',    SST :'+02:00',
    SWT   :'+01:00',    UTC  :'+00:00',    WADT :'+08:00',    WAST   :'+07:00',    WAT   :'-01:00',    WDT   :'+09:00',    WET :'+00:00',
    WETDST:'+01:00',    WST  :'+08:00',    ZP11 :'+11:00',    'ZP-11':'-11:00',    'ZP-2':'-02:00',    'ZP-3':'-03:00',    ZP4 :'+04:00',
    ZP5   :'+05:00',    ZP6  :'+06:00'
  };
  return TimeZoneCodeOffset[timeZoneCode] || '+00:00' // default if not found
};


  // Parameter codes used in NWD
  const ParameterCodes = {
    // water level - chosen from database lookup table query (all possible parameters):
    //   SELECT [Code],[Group],[Unit],[Name] FROM [cwis-1.0].[dbo].[luParameter] WHERE
    //       ( [Name] LIKE '%gage height%' OR [Name] LIKE '%stage%' OR [Name] LIKE '%elevation%' OR [Name] LIKE '%level%' OR [Name] LIKE '%depth%' ) AND
    //       [Unit] IN ('ft','feet','m','meters')
    //   ORDER BY 1;
    // ** indicates in cwis 2021.06.09
    '00062': { code:'00062',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Elevation of reservoir water surface above datum, feet' }, // **
    '00065': { code:'00065',  keywords:['waterLevel','floodStage' ],  unit:'ft'        ,  name:'Gage height, feet' }, // **
    '00072': { code:'00072',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Stream stage, meters' },
    '30207': { code:'30207',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Gage height, above datum, meters' },
    '30210': { code:'30210',  keywords:['waterLevel','waterDepth' ],  unit:'m'         ,  name:'Depth to water level, below land surface datum (LSD), meters' },
    '30211': { code:'30211',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Elevation above NGVD 1929, meters' },
    '30212': { code:'30212',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Stage, sonic range determination, feet' },
    '30213': { code:'30213',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Stage, sonic range determination, meters' },
    '62600': { code:'62600',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Water level elevation above NGVD 1929, corrected for barometric pressure, feet' },
    '62601': { code:'62601',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Water level elevation above NGVD 1929, inclined (non-vertical) well, feet' },
    '62610': { code:'62610',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above NGVD 1929, feet' }, // **
    '62611': { code:'62611',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above NAVD 1988, feet' }, // **
    '62612': { code:'62612',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Groundwater level above NGVD 1929, meters' },
    '62613': { code:'62613',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Groundwater level above NAVD 1988, meters' },
    '62614': { code:'62614',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Lake or reservoir water surface elevation above NGVD 1929, feet' }, // **
    '62615': { code:'62615',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Lake or reservoir water surface elevation above NAVD 1988, feet' }, // **
    '62616': { code:'62616',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Lake or reservoir water surface elevation above NGVD 1929, meters' }, // **
    '62617': { code:'62617',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Lake or reservoir water surface elevation above NAVD 1988, meters' },
    '62618': { code:'62618',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Lake or reservoir stage, above datum, meters' },
    '62619': { code:'62619',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Estuary or ocean water surface elevation above NGVD 1929, feet' }, // **
    '62620': { code:'62620',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Estuary or ocean water surface elevation above NAVD 1988, feet' }, // **
    '62621': { code:'62621',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Estuary or ocean water surface elevation above NGVD 1929, meters' },
    '62622': { code:'62622',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Estuary or ocean water surface elevation above NAVD 1988, meters' },
    '62623': { code:'62623',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Tide stage, above datum, feet' }, // **
    '62624': { code:'62624',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Tide stage, above datum, meters' },
    '63158': { code:'63158',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Stream water level elevation above NGVD 1929, in feet' }, // **
    '63159': { code:'63159',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Stream water level elevation above NGVD 1929, in meters' },
    '63160': { code:'63160',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Stream water level elevation above NAVD 1988, in feet' }, // **
    '63161': { code:'63161',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Stream water level elevation above NAVD 1988, in meters' },
    '72019': { code:'72019',  keywords:['waterLevel','waterDepth' ],  unit:'ft'        ,  name:'Depth to water level, feet below land surface' }, // **
    '72020': { code:'72020',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Elevation above NGVD 1929, feet' }, // **
    '72150': { code:'72150',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level relative to Mean Sea Level (MSL), feet' }, // **
    '72170': { code:'72170',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Stage, tidally filtered, above datum, feet' },
    '72171': { code:'72171',  keywords:['waterLevel'              ],  unit:'meters'    ,  name:'Stage, tidally filtered, above datum, meters' },
    '72178': { code:'72178',  keywords:['waterLevel'              ],  unit:'meters'    ,  name:'Water depth, water surface to bottom, meters' },
    '72199': { code:'72199',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Water depth, water surface to bottom, feet' }, // **
    '72214': { code:'72214',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Lake or reservoir elevation above International Great Lakes Datum (IGLD), feet' }, // **
    '72215': { code:'72215',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Lake or reservoir elevation above International Great Lakes Datum (IGLD), meters' },
    '72226': { code:'72226',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above American Samoa Datum of 1962 (retired in 2001), feet' },
    '72227': { code:'72227',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above American Samoa Vertical Datum of 2002, feet' },
    '72228': { code:'72228',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above Guam Vertical Datum of 1963 (retired in 2003), feet' },
    '72229': { code:'72229',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above Guam Vertical Datum of 2004, feet' }, // **
    '72230': { code:'72230',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above Local Hawaiian Datum, feet' },
    '72231': { code:'72231',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above Northern Marianas Vertical Datum of 2003, feet' },
    '72232': { code:'72232',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Groundwater level above Puerto Rico Datum of 2002, feet' },
    '72251': { code:'72251',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Water level above marsh, feet' }, // **
    '72264': { code:'72264',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Lake or reservoir elevation above New York State Barge Canal Datum (NYBCD), feet' }, // **
    '72265': { code:'72265',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Lake or reservoir elevation above New York State Barge Canal Datum (NYBCD), meters' },
    '72275': { code:'72275',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Lake or reservoir elevation above United States Bureau of Reclamation Klamath Basin (USBRKB) Datum, feet' }, // **
    '72276': { code:'72276',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Tidal water level, NOS-averaged, distance from measuring point to water surface, feet' },
    '72279': { code:'72279',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Tidal elevation, NOS-averaged, NAVD88, feet' }, // **
    '72292': { code:'72292',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Water level elevation above NAVD 1988, corrected for barometric pressure, feet' },
    '72293': { code:'72293',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Water level elevation above gage datum, corrected for barometric pressure, feet' },
    '72335': { code:'72335',  keywords:['waterLevel'              ],  unit:'ft'        ,  name:'Elevation above NAVD 1988, feet' }, // **
    '72336': { code:'72336',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Elevation above NAVD 1988, meters' },
    '99019': { code:'99019',  keywords:['waterLevel','waterDepth' ],  unit:'m'         ,  name:'Water level, depth below land surface, meters' },
    '99020': { code:'99020',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Elevation above NGVD 1929, meters' },
    '99065': { code:'99065',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Gage height, above datum, meters' }, // **
    '99227': { code:'99227',  keywords:['waterLevel'              ],  unit:'m'         ,  name:'Primary water level, Aquatrak, distance from measuring point to water surface, meters' },
    // water flow - chosen from database lookup table query (all possible parameters):
    //   SELECT [Code],[Group],[Unit],[Name] FROM [cwis-1.0].[dbo].[luParameter] WHERE [Name] LIKE '%flow%' OR [Name] LIKE '%discharge%' ORDER BY 1;
    // ** indicates in cwis 2021.06.09
    '00056': { code:'00056',  keywords:['waterFlow'               ],  unit:'gal/day'   ,  name:'Flow rate of well, gallons per day' },
    '00058': { code:'00058',  keywords:['waterFlow'               ],  unit:'gal/min'   ,  name:'Flow rate of well, gallons per minute' }, // **
    '00059': { code:'00059',  keywords:['waterFlow'               ],  unit:'gal/min'   ,  name:'Flow rate, instantaneous, gallons per minute' },
    '00060': { code:'00060',  keywords:['waterFlow'               ],  unit:'ft3/s'     ,  name:'Discharge, cubic feet per second' }, // **
    '00061': { code:'00061',  keywords:['waterFlow'               ],  unit:'ft3/s'     ,  name:'Discharge, instantaneous, cubic feet per second' }, // **
    '30208': { code:'30208',  keywords:['waterFlow'               ],  unit:'m3/sec'    ,  name:'Discharge, cubic meters per second' }, // **
    '30209': { code:'30209',  keywords:['waterFlow'               ],  unit:'m3/sec'    ,  name:'Discharge, instantaneous, cubic meters per second' },
    '50042': { code:'50042',  keywords:['waterFlow'               ],  unit:'gal/min'   ,  name:'Discharge, gallons per minute' }, // **
    '50050': { code:'50050',  keywords:['waterFlow'               ],  unit:'mgd'       ,  name:'Flow, in conduit or through a treatment plant, million gallons per day' }, // **
    '50051': { code:'50051',  keywords:['waterFlow'               ],  unit:'mgd'       ,  name:'Flow rate, instantaneous, million gallons per day' },
    '72137': { code:'72137',  keywords:['waterFlow'               ],  unit:'ft3/s'     ,  name:'Discharge, tidally filtered, cubic feet per second' },
    '72138': { code:'72138',  keywords:['waterFlow'               ],  unit:'m3/sec'    ,  name:'Discharge, tidally filtered, cubic meters per second' },
    '72139': { code:'72139',  keywords:['waterFlow'               ],  unit:'gal/min'   ,  name:'Discharge, tidally filtered, gallons per minute' },
    '72177': { code:'72177',  keywords:['waterFlow'               ],  unit:'cm/d'      ,  name:'Groundwater discharge, centimeters per day' },
    '72243': { code:'72243',  keywords:['waterFlow'               ],  unit:'ft3/day'   ,  name:'Discharge, cubic feet per day' },
    '74072': { code:'74072',  keywords:['waterFlow'               ],  unit:'l/sec'     ,  name:'Flow rate, liters per second' }, // **
    '81395': { code:'81395',  keywords:['waterFlow'               ],  unit:'mgd'       ,  name:'Storm water flow, million gallons per day' },
    '99060': { code:'99060',  keywords:['waterFlow'               ],  unit:'m3/sec'    ,  name:'Discharge, cubic meters per second' },
    '99061': { code:'99061',  keywords:['waterFlow'               ],  unit:'m3/sec'    ,  name:'Discharge, instantaneous, cubic meters per second' },
    // water velocity - chosen from database lookup table query (all possible parameters):
    //   SELECT [Code],[Group],[Unit],[Name] FROM [cwis-1.0].[dbo].[luParameter] WHERE [Name] LIKE '%velocity%' ORDER BY 1;
    // ** indicates in cwis 2021.06.09
    '00055': { code:'00055',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Stream velocity, feet per second' }, // **
    '70232': { code:'70232',  keywords:['waterVelocity'           ],  unit:'knots'     ,  name:'Water velocity reading from field sensor, knots' }, // **
    '72149': { code:'72149',  keywords:['waterVelocity'           ],  unit:'m/sec'     ,  name:'Stream velocity, meters per second' },
    '72168': { code:'72168',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Stream velocity, tidally filtered, feet per second' },
    '72169': { code:'72169',  keywords:['waterVelocity'           ],  unit:'m/s'       ,  name:'Stream velocity, tidally filtered, meters per second' },
    '72190': { code:'72190',  keywords:['waterVelocity'           ],  unit:'m/sec'     ,  name:'Velocity at point in stream, meters per second' },
    '72254': { code:'72254',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Water velocity reading from field sensor, feet per second' }, // **
    '72255': { code:'72255',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Mean water velocity for discharge computation, feet per second' }, // **
    '72294': { code:'72294',  keywords:['waterVelocity'           ],  unit:'mph'       ,  name:'Mean water velocity for discharge computation, miles per hour' }, // **
    '72321': { code:'72321',  keywords:['waterVelocity'           ],  unit:'mph'       ,  name:'Stream velocity, miles per hour' },
    '72322': { code:'72322',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Surface velocity at point in stream, feet per second' }, // **
    '72323': { code:'72323',  keywords:['waterVelocity'           ],  unit:'m/sec'     ,  name:'Surface velocity at point in stream, meters per second' },
    '81380': { code:'81380',  keywords:['waterVelocity'           ],  unit:'m/sec'     ,  name:'Discharge velocity, meters per second' },
    '81904': { code:'81904',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Velocity at point in stream, feet per second' }, // **
    '99240': { code:'99240',  keywords:['waterVelocity'           ],  unit:'ft/sec'    ,  name:'Acoustic Doppler Velocity Meter standard error of velocity, feet per second' },
    '99242': { code:'99242',  keywords:['waterVelocity'           ],  unit:'cm/sec'    ,  name:'Acoustic Doppler Velocity Meter standard error of velocity, centimeters per second' },
    '99272': { code:'99272',  keywords:['waterVelocity'           ],  unit:'ft/day'    ,  name:'Groundwater flow velocity, feet per day' },
    // water quality - chosen from inventory of distinct parameters currently in system (not all possible parameters):
    //   [CWIS_ROOT_URL]/odata/CurrentConditions?$apply=groupby((ParameterCode,ParameterGroup,ParameterName,ParameterUnit),aggregate($count+as+Count))&$orderby=ParameterCode
    // updated 2021.05.18
    '32217': { code:'32217',  keywords:['waterChlorophyll'        ],  unit:'ug/l'      ,  name:'Chlorophyll a, fluorometric method, uncorrected, micrograms per liter' },
    '32283': { code:'32283',  keywords:['waterChlorophyll'        ],  unit:'RFU'       ,  name:'Chlorophyll fluorescence (fChl), total, water, in situ, fluorometric, 650-700 nm, relative fluorescence units (RFU)' },
    '32284': { code:'32284',  keywords:['waterChlorophyll'        ],  unit:'ug/l'      ,  name:'Chlorophyll a, total, in situ, fluorescence excitation at 370, 470, 525, 570, 590, 610 nm, fluorescence emission at 700 nm with correction for CDOM, ug/L' },
    '32315': { code:'32315',  keywords:['waterChlorophyll'        ],  unit:'RFU'       ,  name:'Chlorophyll relative fluorescence (fChl), water, in situ, relative fluorescence units (RFU)' },
    '32316': { code:'32316',  keywords:['waterChlorophyll'        ],  unit:'ug/l'      ,  name:'Chlorophyll fluorescence (fChl), water, in situ, concentration estimated from reference material, micrograms per liter as chlorophyll' },
    '32318': { code:'32318',  keywords:['waterChlorophyll'        ],  unit:'ug/l'      ,  name:'Chlorophylls, water, in situ, fluorometric method, excitation at 470 +-15 nm, emission at 685 +-20 nm, micrograms per liter' },
    '32320': { code:'32320',  keywords:['waterChlorophyll'        ],  unit:'RFU'       ,  name:'Chlorophyll fluorescence (fChl), water, in situ, fluorometric method, excitation at 470 +-15 nm, emission at 685 +-20 nm, relative fluorescence units (RFU)' },
    '62361': { code:'62361',  keywords:['waterChlorophyll'        ],  unit:'ug/l'      ,  name:'Chlorophyll, total, water, fluorometric, 650-700 nanometers, in situ sensor, micrograms per liter' },
    '00300': { code:'00300',  keywords:['waterDissolvedOxygen'    ],  unit:'mg/l'      ,  name:'Dissolved oxygen, water, unfiltered, milligrams per liter' },
    '00301': { code:'00301',  keywords:['waterDissolvedOxygen'    ],  unit:'% saturatn',  name:'Dissolved oxygen, water, unfiltered, percent of saturation' },
    '63689': { code:'63689',  keywords:['waterMajorInorganics'    ],  unit:'mg/l'      ,  name:'Bromide, water, unfiltered, milligrams per liter' },
    '99141': { code:'99141',  keywords:['waterMajorInorganics'    ],  unit:'mg/l'      ,  name:'Carbon dioxide, water, dissolved, in situ, milligrams per liter' },
    '99220': { code:'99220',  keywords:['waterMajorInorganics'    ],  unit:'mg/l'      ,  name:'Chloride, water, unfiltered, milligrams per liter' },
    '99398': { code:'99398',  keywords:['waterMajorInorganics'    ],  unit:'mg/l'      ,  name:'Sodium, water, filtered, estimated by regression equation, milligrams per liter' },
    '99404': { code:'99404',  keywords:['waterMajorInorganics'    ],  unit:'mg/l'      ,  name:'Chloride, water, filtered, estimated by regression equation, milligrams per liter' },
    '00630': { code:'00630',  keywords:['waterNutrients'          ],  unit:'mg/l as N' ,  name:'Nitrate plus nitrite, water, unfiltered, milligrams per liter as nitrogen' },
    '00665': { code:'00665',  keywords:['waterNutrients'          ],  unit:'mg/l as P' ,  name:'Phosphorus, water, unfiltered, milligrams per liter as phosphorus' },
    '00666': { code:'00666',  keywords:['waterNutrients'          ],  unit:'mg/l as P' ,  name:'Phosphorus, water, filtered, milligrams per liter as phosphorus' },
    '51289': { code:'51289',  keywords:['waterNutrients'          ],  unit:'mg/l as P' ,  name:'Orthophosphate, water, in situ, milligrams per liter as phosphorus' },
    '91049': { code:'91049',  keywords:['waterNutrients'          ],  unit:'lb/d as N' ,  name:'Nitrate plus nitrite, water, unfiltered, pounds of nitrogen per day' },
    '91058': { code:'91058',  keywords:['waterNutrients'          ],  unit:'lb/day'    ,  name:'Total nitrogen [nitrate + nitrite + ammonia + organic-N], water, unfiltered, pounds of nitrogen per day' },
    '99133': { code:'99133',  keywords:['waterNutrients'          ],  unit:'mg/l as N' ,  name:'Nitrate plus nitrite, water, in situ, milligrams per liter as nitrogen' },
    '99137': { code:'99137',  keywords:['waterNutrients'          ],  unit:'mg/l as N' ,  name:'Nitrate, water, in situ, milligrams per liter as nitrogen' },
    '32295': { code:'32295',  keywords:['waterOrganics'           ],  unit:'ug/l QSE'  ,  name:'Dissolved organic matter fluorescence (fDOM), water, in situ, concentration estimated from reference material, micrograms per liter as quinine sulfate equivalents (QSE)' },
    '32322': { code:'32322',  keywords:['waterOrganics'           ],  unit:'RFU'       ,  name:'Dissolved organic matter relative fluorescence (fDOM), water, in situ, relative fluorescence units (RFU)' },
    '32330': { code:'32330',  keywords:['waterOrganics'           ],  unit:'mg/l'      ,  name:'Colored dissolved organic matter fluorescence (fDOM), water, in situ, milligrams per liter of carbon' },
    '99134': { code:'99134',  keywords:['waterOrganics'           ],  unit:'mg/l'      ,  name:'Dissolved organic carbon (DOC), water, in situ, concentration computed by regression of sensor data with field sample lab results, milligrams per liter as carbon' },
    '00400': { code:'00400',  keywords:['waterPH'                 ],  unit:'std units' ,  name:'pH, water, unfiltered, field, standard units' },
    '00095': { code:'00095',  keywords:['waterSpecificConductance'],  unit:'uS/cm @25C',  name:'Specific conductance, water, unfiltered, microsiemens per centimeter at 25 degrees Celsius' },
    '80295': { code:'80295',  keywords:['waterSuspendedSediment'  ],  unit:'lb/s'      ,  name:'Suspended sediment load, water, unfiltered, estimated by regression equation, pounds per second' },
    '80297': { code:'80297',  keywords:['waterSuspendedSediment'  ],  unit:'tons/day'  ,  name:'Suspended sediment load, water, unfiltered, computed, the product of regression-computed suspended sediment concentration and streamflow, short tons per day' },
    '80299': { code:'80299',  keywords:['waterSuspendedSediment'  ],  unit:'mg/l'      ,  name:'Suspended sediment concentration, from uncalibrated sensor, milligrams per liter' },
    '99409': { code:'99409',  keywords:['waterSuspendedSediment'  ],  unit:'mg/l'      ,  name:'Suspended sediment concentration, water, unfiltered, estimated by regression equation, milligrams per liter' },
    '00010': { code:'00010',  keywords:['waterTemperature'        ],  unit:'deg C'     ,  name:'Temperature, water, degrees Celsius' },
    '00011': { code:'00011',  keywords:['waterTemperature'        ],  unit:'deg F'     ,  name:'Temperature, water, degrees Fahrenheit' },
    '85583': { code:'85583',  keywords:['waterTemperature'        ],  unit:'deg C'     ,  name:'Temperature, intragravel water, degrees Celsius' },
    '00076': { code:'00076',  keywords:['waterTurbidity'          ],  unit:'NTU'       ,  name:'Turbidity, water, unfiltered, nephelometric turbidity units' },
    '63680': { code:'63680',  keywords:['waterTurbidity'          ],  unit:'FNU'       ,  name:'Turbidity, water, unfiltered, monochrome near infra-red LED light, 780-900 nm, detection angle 90 +-2.5 degrees, formazin nephelometric units (FNU)' },
    '63682': { code:'63682',  keywords:['waterTurbidity'          ],  unit:'FBU'       ,  name:'Turbidity, water, unfiltered, monochrome near infra-red LED light source, 780-900 nm, detection angle 0-45 degrees to incident light (backscatter), FBU' },
    '72213': { code:'72213',  keywords:['waterTurbidity'          ],  unit:'FBRU'      ,  name:'Turbidity, water, unfiltered, monochrome near infra-red LED light, 780-900 nm, detector angles at 90 +-2.5 degrees and 0-45 degrees, ratiometric, FBRU' },
    '72337': { code:'72337',  keywords:['waterTurbidity'          ],  unit:'SBU'       ,  name:'Turbidity, water, unfiltered, monochrome near infra-red LED light, 780-900 nm, detection angle 135-180 degrees, SDVB (styrene divinylbenzene beads) backscatter units(SBU)' },
    '00047': { code:'00047',  keywords:['waterQualityOther'       ],  unit:'mm/Hg'     ,  name:'Total partial pressure of dissolved gases, water, unfiltered, millimeters of mercury' },
    '00048': { code:'00048',  keywords:['waterQualityOther'       ],  unit:'%'         ,  name:'Total partial pressure of dissolved gases, water, unfiltered, percent of saturation' },
    '00401': { code:'00401',  keywords:['waterQualityOther'       ],  unit:'meq'       ,  name:'Cations minus anions, water, milliequivalents' },
    '00480': { code:'00480',  keywords:['waterQualityOther'       ],  unit:'ppth'      ,  name:'Salinity, water, unfiltered, parts per thousand' },
    '32319': { code:'32319',  keywords:['waterQualityOther'       ],  unit:'ug/l'      ,  name:'Phycocyanin fluorescence (fPC), water, in situ, concentration estimated from reference material, micrograms per liter as phycocyanin' },
    '32321': { code:'32321',  keywords:['waterQualityOther'       ],  unit:'RFU'       ,  name:'Phycocyanin relative fluorescence (fPC), water, in situ, relative fluorescence units (RFU)' },
    '32323': { code:'32323',  keywords:['waterQualityOther'       ],  unit:'RFU'       ,  name:'Phycoerythrin relative fluorescence (fPE), water, in situ, relative fluorescence units (RFU)' },
    '32341': { code:'32341',  keywords:['waterQualityOther'       ],  unit:'ug/L PTSA' ,  name:'Optical brightener fluorescence (fOB), water, in situ, concentration estimated from reference material, micrograms per liter as 1,3,6,8-pyrene tetrasulfonic acid (PTSA)' },
    '90860': { code:'90860',  keywords:['waterQualityOther'       ],  unit:'PSU'       ,  name:'Salinity, water, unfiltered, practical salinity units at 25 degrees Celsius' },
    '95202': { code:'95202',  keywords:['waterQualityOther'       ],  unit:'#/ml'      ,  name:'Cyanobacteria fluorescence of phycocyanin (fPC), water, in situ, concentration estimated from reference material, cells per milliliter' },
    '95204': { code:'95204',  keywords:['waterQualityOther'       ],  unit:'IVFU'      ,  name:'Phycocyanins (cyanobacteria), water, in situ, in vivo fluorescence, in vivo fluorescence units' },
    '99407': { code:'99407',  keywords:['waterQualityOther'       ],  unit:'cfu/100ml' ,  name:'Escherichia coli, estimated by regression equation, water, colonies per 100 milliliters' },
    // rain - chosen from inventory of distinct parameters currently in system (not all possible parameters):
    //   [CWIS_ROOT_URL]/odata/CurrentConditions?$apply=groupby((ParameterCode,ParameterGroup,ParameterName,ParameterUnit),aggregate($count+as+Count))&$orderby=ParameterCode
    // updated 2021.05.18
    '00045': { code:'00045',  keywords:['rain'                    ],  unit:'in'        ,  name:'Precipitation, total, inches' },
    '00193': { code:'00193',  keywords:['rain'                    ],  unit:'in'        ,  name:'Precipitation total for defined period, inches' },
    '72192': { code:'72192',  keywords:['rain'                    ],  unit:'in'        ,  name:'Precipitation, cumulative, inches' },
    '72194': { code:'72194',  keywords:['rain'                    ],  unit:'mm'        ,  name:'Precipitation, cumulative, millimeters' },
    '99772': { code:'99772',  keywords:['rain'                    ],  unit:'mm'        ,  name:'Precipitation, millimeters' },
    // atmospheric - chosen from inventory of distinct parameters currently in system (not all possible parameters):
    //   [CWIS_ROOT_URL]/odata/CurrentConditions?$apply=groupby((ParameterCode,ParameterGroup,ParameterName,ParameterUnit),aggregate($count+as+Count))&$orderby=ParameterCode
    // updated 2021.05.18
    '00020': { code:'00020',  keywords:['airTemperature'          ],  unit:'deg C'     ,  name:'Temperature, air, degrees Celsius' },
    '00021': { code:'00021',  keywords:['airTemperature'          ],  unit:'deg F'     ,  name:'Temperature, air, degrees Fahrenheit' },
    '00025': { code:'00025',  keywords:['barometricPressure'      ],  unit:'mm/Hg'     ,  name:'Barometric pressure, millimeters of mercury' },
    '62602': { code:'62602',  keywords:['barometricPressure'      ],  unit:'in/Hg'     ,  name:'Barometric pressure, corrected to sea level, inches of mercury' },
    '62603': { code:'62603',  keywords:['barometricPressure'      ],  unit:'in/Hg'     ,  name:'Barometric pressure, uncorrected, inches of mercury' },
    '62605': { code:'62605',  keywords:['barometricPressure'      ],  unit:'in/H2O'    ,  name:'Barometric pressure, uncorrected, inches of water' },
    '62607': { code:'62607',  keywords:['barometricPressure'      ],  unit:'kPa'       ,  name:'Barometric pressure, uncorrected, kilopascals' },
    '72204': { code:'72204',  keywords:['barometricPressure'      ],  unit:'psi'       ,  name:'Barometric pressure (BP), uncorrected, pounds per square inch' },
    '75969': { code:'75969',  keywords:['barometricPressure'      ],  unit:'mbar'      ,  name:'Barometric pressure, not corrected to sea level, millibars' },
    '72130': { code:'72130',  keywords:['evapotranspiration'      ],  unit:'mm/hr'     ,  name:'Potential evapotranspiration (PET), calculated by Penman method, millimeters per hour' },
    '72159': { code:'72159',  keywords:['evapotranspiration'      ],  unit:'mm/d'      ,  name:'Evapotranspiration, millimeters per day' },
    '00052': { code:'00052',  keywords:['relativeHumidity'        ],  unit:'%'         ,  name:'Relative humidity, percent' },
    '00030': { code:'00030',  keywords:['solarRadiation'          ],  unit:'cal/cm2/d' ,  name:'Incident solar radiation intensity, calories per square centimeter per day' },
    '62608': { code:'62608',  keywords:['solarRadiation'          ],  unit:'W/m2'      ,  name:'Total solar radiation (direct + diffuse radiation on a horizontal surface), watts per square meter' },
    '62609': { code:'62609',  keywords:['solarRadiation'          ],  unit:'W/m2'      ,  name:'Net solar radiation, watts per square meter' },
    '72124': { code:'72124',  keywords:['solarRadiation'          ],  unit:'W/m2'      ,  name:'Net radiation (net solar + net long wave radiation), watts per square meter' },
    '72252': { code:'72252',  keywords:['solarRadiation'          ],  unit:'kW/m2'     ,  name:'Solar radiation (average flux density on a horizontal surface during measurement interval), kilowatts per square meter' },
    '99986': { code:'99986',  keywords:['solarRadiation'          ],  unit:'W/m2'      ,  name:'Solar radiation (average flux density on a horizontal surface during measurement interval), watts per square meter' },
    '99987': { code:'99987',  keywords:['solarRadiation'          ],  unit:'MJ/m2'     ,  name:'Solar radiation (total flux density on a horizontal surface during measurement interval), megajoules per square meter' },
    '00036': { code:'00036',  keywords:['windDirection'           ],  unit:'Deg'       ,  name:'Wind direction, degrees clockwise from true north' },
    '61729': { code:'61729',  keywords:['windDirection'           ],  unit:'Deg'       ,  name:'Wind gust direction, air, degrees clockwise from true north' },
    '00035': { code:'00035',  keywords:['windSpeed'               ],  unit:'mph'       ,  name:'Wind speed, miles per hour' },
    '61727': { code:'61727',  keywords:['windSpeed'               ],  unit:'knots'     ,  name:'Wind gust speed, air, knots' },
    '61728': { code:'61728',  keywords:['windSpeed'               ],  unit:'mph'       ,  name:'Wind gust speed, air, miles per hour' },
    '62625': { code:'62625',  keywords:['windSpeed'               ],  unit:'m/sec'     ,  name:'Wind speed, meters per second' },
    '72269': { code:'72269',  keywords:['windSpeed'               ],  unit:'m/sec'     ,  name:'Wind gust speed, air, meters per second' },
    '82127': { code:'82127',  keywords:['windSpeed'               ],  unit:'knots'     ,  name:'Wind speed, knots' },
    '00201': { code:'00201',  keywords:['atmosphericOther'        ],  unit:'uEinst/m2' ,  name:'Incident light, daily total, 400-700 nanometers, microeinsteins per square meter' },
    '62968': { code:'62968',  keywords:['atmosphericOther'        ],  unit:'W/m2'      ,  name:'Latent-heat flux, watts per square meter' },
    '62969': { code:'62969',  keywords:['atmosphericOther'        ],  unit:'W/m2'      ,  name:'Sensible-heat flux, watts per square meter' },
    '72125': { code:'72125',  keywords:['atmosphericOther'        ],  unit:'kPa'       ,  name:'Atmospheric water vapor pressure, calculated, kilopascals' },
    '72186': { code:'72186',  keywords:['atmosphericOther'        ],  unit:'W/m2'      ,  name:'Shortwave radiation, downward intensity, watts per square meter' },
    '72201': { code:'72201',  keywords:['atmosphericOther'        ],  unit:'W/m2'      ,  name:'Net incident shortwave radiation, watts per square meter' },
    '72202': { code:'72202',  keywords:['atmosphericOther'        ],  unit:'W/m2'      ,  name:'Net emitted longwave radiation, watts per square meter' },
    '99988': { code:'99988',  keywords:['atmosphericOther'        ],  unit:'umol/m2/s' ,  name:'Photosynthetically active radiation (average flux density on a horizontal surface during measurement interval), micromoles of photons per square meter per second' },
    '99989': { code:'99989',  keywords:['atmosphericOther'        ],  unit:'mmol/m2'   ,  name:'Photosynthetically active radiation (total flux density on a horizontal surface during measurement interval), millimoles of photons per square meter' },
    // soil - chosen from inventory of distinct parameters currently in system (not all possible parameters):
    //   [CWIS_ROOT_URL]/odata/CurrentConditions?$apply=groupby((ParameterCode,ParameterGroup,ParameterName,ParameterUnit),aggregate($count+as+Count))&$orderby=ParameterCode
    // updated 2021.05.18
    '54153': { code:'54153',  keywords:['soilCarbonDioxide'       ],  unit:'% by vol'  ,  name:'Carbon dioxide, soil gas, uncorrected for temperature and pressure, percent of soil gas' },
    '54158': { code:'54158',  keywords:['soilCarbonDioxide'       ],  unit:'% by vol'  ,  name:'Carbon dioxide, soil gas, corrected for temperature and pressure, percent of soil gas' },
    '72205': { code:'72205',  keywords:['soilConductance'         ],  unit:'dS/m'      ,  name:'Bulk electrical conductance, soil, decisiemens per meter' },
    '72181': { code:'72181',  keywords:['soilMoisture'            ],  unit:'number'    ,  name:'Moisture content, soil, volumetric, fraction of total volume' },
    '74207': { code:'74207',  keywords:['soilMoisture'            ],  unit:'%'         ,  name:'Moisture content, soil, volumetric, percent of total volume' },
    '54152': { code:'54152',  keywords:['soilOxygen'              ],  unit:'% by vol'  ,  name:'Oxygen, soil gas, uncorrected for temperature and pressure, percent of soil gas' },
    '72259': { code:'72259',  keywords:['soilPermittivity'        ],  unit:'None'      ,  name:'Dielectric permittivity, soil, in situ' },
    '62846': { code:'62846',  keywords:['soilTemperature'         ],  unit:'deg F'     ,  name:'Temperature, soil, degrees Fahrenheit' },
    '72253': { code:'72253',  keywords:['soilTemperature'         ],  unit:'deg C'     ,  name:'Soil temperature, degrees Celsius' },
    null   : { code:null   ,  keywords:[                          ],  unit:'NA'        ,  name:'Unspecified measurement parameter' }
  };

// returns metadata associated with a parameter code
// adapted from https://code.usgs.gov/noms/nwd/-/blob/master/deploy/lib/L-cwis-2021.09.01/L-cwis.js
export const getParameterCodeInfo = (pcode) => {

  return ParameterCodes[pcode] || { // default to generic
    code     : pcode+'',
    keywords : [],
    unit     : 'NA',
    name     : `Parameter ${pcode}`
  };
};

// returns description associated with a value flag code
// adapted from https://code.usgs.gov/noms/nwd/-/blob/master/deploy/lib/L-cwis-2021.09.01/L-cwis.js
export const getValueFlagDescription = (valueFlagCode) => {

  // value flag codes - [cwis].[luValueFlag] table
  const valueFlagCodes = {
    '***': { code:'***' ,  description:'Value is flagged'            },
    'BKW': { code:'BKW' ,  description:'Affected by backwater'       },
    'DIS': { code:'DIS' ,  description:'Discontinued record'         },
    'DRY': { code:'DRY' ,  description:'Dry condition'               },
    'EQP': { code:'EQP' ,  description:'Equipment malfunction'       },
    'FLD': { code:'FLD' ,  description:'Flooding conditions'         },
    'ICE': { code:'ICE' ,  description:'Affected by ice'             },
    'MNT': { code:'MNT' ,  description:'Maintenance'                 },
    'PMP': { code:'PMP' ,  description:'Affected by pumping'         },
    'PR' : { code:'PR'  ,  description:'Partial monitoring'          },
    'RAT': { code:'RAT' ,  description:'Rating being developed'      },
    'SSN': { code:'SSN' ,  description:'Seasonal monitoring'         },
    'TST': { code:'TST' ,  description:'Affected by test conditions' },
    'ZFL': { code:'ZFL' ,  description:'Zero flow condition'         },
    null : { code:null  ,  description:'No value flag'               }
  };

  valueFlagCode && console.log(valueFlagCode);

  return valueFlagCodes[valueFlagCode] || valueFlagCodes['***']; // default 'value flagged'

};

 // get parameter code associated with primary site category (if other, return null)
 export const getParameterCodes = (siteId, layerId, layer, dataSlice) => {
  // handle case where data sources display primary site category values differently (e.g. "SW" or "surface water")
  // https://stackoverflow.com/questions/14743536/multiple-key-names-same-pair-value
  function expand(obj) {
    var keys = Object.keys(obj);
    for (var i = 0; i < keys.length; ++i) {
      var key = keys[i],
        subkeys = key.split(/,\s?/),
        target = obj[key];
      delete obj[key];
      subkeys.forEach(function (key) {
        obj[key] = target;
      });
    }
    return obj;
  }

  // commas separate all possible keys that different data sources may use
  let parameterCodes = {
    "surface water, SW": [
      { code: "00065", label: "Gage height, feet", priority: 1 },
      {
        code: "00060",
        label: "Discharge, cubic feet per second",
        priority: 2,
      },
    ],
    "groundwater, GW": [
      {
        code: "72019",
        label: "Depth to water level, feet below land surface",
        priority: 1,
      },
    ],
    "atmospheric, AT": [
      { code: "00045", label: "Precipitation, total, inches", priority: 1 },
    ],
    other: [
      { code: "00065", label: "Gage height, feet", priority: 1 },
      {
        code: "00060",
        label: "Discharge, cubic feet per second",
        priority: 2,
      },
      {
        code: "72019",
        label: "Depth to water level, feet below land surface",
        priority: 3,
      },
      { code: "00045", label: "Precipitation, total, inches", priority: 1 },
    ],
  };

  // expand parameterCodes - one key per value
  parameterCodes = expand(parameterCodes);

  const dataName = layer[layerId].data;
  if (dataSlice[dataName].data !== null) {
    const site = dataSlice[dataName].data.features.find(
      (feat) =>
        feat.properties[layer[layerId].siteNumberField] === siteId
    );
    const primarySiteCategory = site.properties[layer[layerId].primarySiteCategoryField]
    if (site && primarySiteCategory) {
      const siteType = primarySiteCategory.toLowerCase();
      return parameterCodes[siteType] ? parameterCodes[siteType] : null;
    }
  }
  return null;
};

export const parseProjectData = (projects) => {
  let tempProjectState = {};
  for (const project of projects) {
    let basin;
    if (project.basin != null){
      basin = project.basin.name;
      if (tempProjectState.hasOwnProperty(basin)) {
        tempProjectState[basin] = [...tempProjectState[basin], project];
      }
      else {
        tempProjectState[basin] = [project];
      }
    }
  }
  return tempProjectState;
};


export const parseNwisTsv = (tsv_string) => {
  const rows = tsv_string.split('\n');
  let data = {}
  let first_row = []
  for (const row of rows) {
    if (row[0] !== "#") {
      if (first_row.length < 1) {
        first_row = row.split('\t');
        for (const column of first_row) {
          data[column] = []
        }
      } else {
        const columns = row.split('\t');
        for (let i = 0; i < first_row.length; i++) {
          data[first_row[i]].push(columns[i])
        }
      }
    }
  }
  return data
}
